import { Injectable, signal } from '@angular/core';
import { initializeApp } from 'firebase/app';
import {
  getRemoteConfig,
  getValue,
  fetchAndActivate,
  RemoteConfig,
} from 'firebase/remote-config';
import {
  firebaseConfig,
  remoteConfigDefaults,
} from '@common/config/firebase.config';
import { PlatformIdentificationService } from './platform-identification.service';

@Injectable({
  providedIn: 'root',
})
export class FirebaseRemoteConfigService {
  private remoteConfig!: RemoteConfig;
  private isInitialized = false;

  // wathiq_rollback - Shared signal for wathiq flow status (centralized)
  turn_on_watheeq_flow = signal<boolean>(false);
  private watheeqFlowInitialized = false;

  constructor(
    private platformIdentificationService: PlatformIdentificationService
  ) {
    this.initializeFirebase();
  }

  private initializeFirebase(): void {
    try {
      const app = initializeApp(firebaseConfig);
      this.remoteConfig = getRemoteConfig(app);

      this.remoteConfig.defaultConfig = remoteConfigDefaults;

      this.remoteConfig.settings.minimumFetchIntervalMillis = 0;

      this.remoteConfig.settings.fetchTimeoutMillis = 10000;

      this.platformIdentificationService.identifyPlatform(app);

      this.isInitialized = true;

      // wathiq_rollback - Initialize wathiq flow status on service init
      this.initWatheeqFlowStatus();
    } catch (error) {
      this.isInitialized = false;
    }
  }

  // wathiq_rollback - Initialize and cache the wathiq flow status
  private async initWatheeqFlowStatus(): Promise<void> {
    if (this.watheeqFlowInitialized) return;

    const status = await this.getTurnOnWatheeqFlowValue();
    this.turn_on_watheeq_flow.set(status);
    this.watheeqFlowInitialized = true;
  }

  async getTurnOnAnalyticsValue(): Promise<boolean> {
    if (!this.isInitialized) {
      return false;
    }

    try {
      await fetchAndActivate(this.remoteConfig);

      const configValue = getValue(this.remoteConfig, 'turn_on_analytics');
      return configValue.asBoolean();
    } catch (error) {
      return false;
    }
  }

  // wathiq_rollback - Get turn_on_watheeq_flow value from Firebase Remote Config
  async getTurnOnWatheeqFlowValue(): Promise<boolean> {
    if (!this.isInitialized) {
      return false;
    }

    try {
      await fetchAndActivate(this.remoteConfig);

      const configValue = getValue(this.remoteConfig, 'turn_on_watheeq_flow');
      return configValue.asBoolean();
    } catch (error) {
      return false;
    }
  }
}
